'use client';

import {
  BarChart3,
  Blocks,
  Inbox,
  PhoneOutgoing,
  ShieldCheck,
  Sparkles,
} from 'lucide-react';

import { useTranslation } from '@/lib/i18n/context';

import { BentoGrid, type BentoCard } from './bento-grid';
import { PRODUCTS_AR, PRODUCTS_SECTION_AR } from './products.ar';

const CARDS: BentoCard[] = [
  {
    id: 'inbound-agent',
    icon: Inbox,
    title: 'Inbound Agent',
    description:
      'Frontline support across every channel: chat, voice, email, WhatsApp, and social. It resolves issues end-to-end, not just answers them.',
    modal: {
      eyebrow: 'Inbound Agent',
      headline: 'Support that resolves, on every channel',
      lead: 'One agent picks up every conversation your customers start, and finishes it. It answers, takes action in your systems, and hands off to a human only when it should.',
      blocks: [
        {
          type: 'text',
          body: [
            'The Inbound Agent is the front line of your support. It handles chat, voice, email, WhatsApp, and social with one brain, one personality, and one source of truth, so customers get the same quality answer no matter where they reach you.',
            'It goes beyond answering: it processes refunds, updates accounts, tracks orders, and escalates with full context, resolving tickets end-to-end inside the same conversation.',
          ],
        },
        {
          type: 'channels',
          heading: 'Every channel it covers',
          subheading:
            'One agent, one knowledge brain, live everywhere your customers reach you. Explore each channel.',
          items: [
            { name: 'Voice', href: '/channels/voice' },
            { name: 'Chat', href: '/channels/chat' },
            { name: 'Email', href: '/channels/email' },
            { name: 'WhatsApp', href: '/channels/whatsapp' },
            { name: 'Slack', href: '/channels/slack' },
            { name: 'Social', href: '/channels/social' },
          ],
        },
        {
          type: 'stats',
          items: [
            { value: '77%', label: 'Avg. automation rate' },
            { value: '100+', label: 'Languages supported' },
            { value: '8+', label: 'Channels, one agent' },
          ],
        },
        {
          type: 'list',
          heading: 'What it handles',
          items: [
            'End-to-end resolutions, not deflections',
            'Real actions: refunds, account changes, order tracking',
            'Context that follows the customer across channels',
            'Context-rich handoffs to your human team',
            'Confidence thresholds that keep accuracy high',
            'Works inside the helpdesk you already run',
          ],
        },
        {
          type: 'text',
          heading: 'Resolution, not deflection',
          body: 'Half of support volume is requests to do something, and a link to a help article resolves none of it. The Inbound Agent calls your APIs, completes the task, and confirms it back in plain language. Anything sensitive pauses for a one-click human approval.',
        },
        {
          type: 'faq',
          heading: 'Frequently asked',
          items: [
            {
              q: 'Which channels does it support?',
              a: 'Chat, voice, email, SMS, WhatsApp, Instagram, Messenger, Slack, and Discord, with native telephony built in. One agent and one knowledge base serve them all.',
            },
            {
              q: 'Does it actually resolve issues, or just answer questions?',
              a: 'It resolves them. The agent calls your APIs to process refunds, update accounts, and track orders, completing the task inside the same conversation.',
            },
            {
              q: 'What happens when it is not confident?',
              a: 'Confidence thresholds decide. Below the bar it hands off to a human with the full conversation, customer context, and every action taken attached.',
            },
            {
              q: 'Do I need to leave my current helpdesk?',
              a: 'No. It works your tickets natively inside Zendesk, Intercom, Salesforce, HubSpot, Freshdesk, Twilio Flex, or Open’s own helpdesk.',
            },
          ],
        },
      ],
      cta: { label: 'See it in action', href: '/demo' },
    },
  },
  {
    id: 'outbound-agent',
    icon: PhoneOutgoing,
    title: 'Outbound Agent',
    description:
      'Multi-channel outbound for sales, marketing, and proactive support. It reaches out before your customers have to.',
    modal: {
      eyebrow: 'Outbound Agent',
      headline: 'Reach out before they reach in',
      lead: 'Most support is reactive by default. The Outbound Agent flips that: it contacts customers across channels for sales follow-ups, marketing campaigns, and proactive support moments.',
      blocks: [
        {
          type: 'text',
          body: [
            'A delayed order, an expiring card, an abandoned signup, a renewal coming due: these are conversations worth starting, not waiting for. The Outbound Agent initiates them on the right channel at the right time, and holds the full back-and-forth that follows.',
            'Because it shares the same brain as the rest of the platform, every outbound conversation carries real customer context, and every reply lands with an agent that can actually take action, not a no-reply address.',
          ],
        },
        {
          type: 'list',
          heading: 'Outbound flows it runs',
          items: [
            'Proactive delay and outage notifications, with live Q&A',
            'Renewal, expiry, and payment-failure follow-ups',
            'Abandoned cart and incomplete-signup recovery',
            'Win-back and re-engagement campaigns',
            'Sales follow-ups after demos, trials, and quotes',
            'CSAT and NPS follow-up conversations',
            'Onboarding check-ins and activation nudges',
            'Announcements that can answer the questions they raise',
          ],
        },
        {
          type: 'channels',
          heading: 'Channels it reaches out on',
          subheading:
            'Voice calls, email, WhatsApp, SMS, and social. Each flow picks the channel your customer actually answers.',
          items: [
            { name: 'Voice', href: '/channels/voice' },
            { name: 'Email', href: '/channels/email' },
            { name: 'WhatsApp', href: '/channels/whatsapp' },
            { name: 'Chat', href: '/channels/chat' },
            { name: 'Social', href: '/channels/social' },
            { name: 'Slack', href: '/channels/slack' },
          ],
        },
        {
          type: 'stats',
          items: [
            { value: '8+', label: 'Outbound channels' },
            { value: '2-way', label: 'Every conversation' },
            { value: '100%', label: 'Context-aware' },
          ],
        },
        {
          type: 'text',
          heading: 'Campaigns that can hold a conversation',
          body: 'A blast tool stops at delivery; the Outbound Agent is there for the reply. When a customer answers “actually, can you move my delivery to Thursday?”, the same agent reschedules it on the spot, because outbound and support run on one brain, not two disconnected tools.',
        },
        {
          type: 'faq',
          heading: 'Frequently asked',
          items: [
            {
              q: 'How is this different from a marketing automation tool?',
              a: 'Blast tools are one-way. The Outbound Agent holds the conversation that follows: it answers questions, takes actions, and resolves issues raised by its own outreach.',
            },
            {
              q: 'Which channels can it reach out on?',
              a: 'Voice calls, email, WhatsApp, SMS, chat, and social. Each flow picks the channel per customer, with fallbacks if they don’t respond.',
            },
            {
              q: 'Can it do sales outreach, not just support?',
              a: 'Yes. It runs follow-ups after demos, trials, and quotes, re-engages cold leads, and books meetings, with full CRM context on every touch.',
            },
            {
              q: 'How do I keep it from spamming customers?',
              a: 'You set the rules: frequency caps, quiet hours, channel preferences, and opt-out handling are enforced across every flow automatically.',
            },
          ],
        },
      ],
      cta: { label: 'See it in action', href: '/demo' },
    },
  },
  {
    id: 'assist-agent',
    icon: Sparkles,
    title: 'Assist Agent',
    description:
      'A copilot for your human team. It fills fields, summarizes conversations, and suggests the next best reply.',
    modal: {
      eyebrow: 'Assist Agent',
      headline: 'Every agent, working at the speed of your best one',
      lead: 'Not every conversation should be automated, but every conversation can be accelerated. The Assist Agent rides along with your human team and does the busywork for them.',
      blocks: [
        {
          type: 'text',
          body: [
            'It drafts replies in your brand voice, summarizes long threads on handoff, fills ticket fields and tags automatically, and surfaces the right knowledge at the right moment, all inside the helpdesk your team already uses.',
            'The result: faster handle times, cleaner data, and a team that spends its energy on judgment calls instead of typing.',
          ],
        },
        {
          type: 'list',
          heading: 'What it handles',
          items: [
            'Suggested replies your team can send or edit',
            'One-click summaries of any conversation',
            'Auto-filled ticket fields, tags, and dispositions',
            'Knowledge surfaced in context, as they type',
            'Tone rewrites and translation across languages',
            'Handoff briefs so nobody reads a 40-message thread',
          ],
        },
        {
          type: 'stats',
          items: [
            { value: '1-click', label: 'Thread summaries' },
            { value: '0', label: 'Tabs to switch' },
            { value: '7+', label: 'Helpdesks supported' },
          ],
        },
        {
          type: 'text',
          heading: 'Inside the tools they already know',
          body: 'The Assist Agent lives in your existing helpdesk: Zendesk, Intercom, Salesforce, HubSpot, Freshdesk, and more. No new tool to learn, no new tab to keep open. It reads the same ticket your agent is looking at and helps right there.',
        },
        {
          type: 'faq',
          heading: 'Frequently asked',
          items: [
            {
              q: 'Does it replace my human agents?',
              a: 'No, it accelerates them. It handles the drafting, summarizing, and data entry so your team focuses on judgment and empathy.',
            },
            {
              q: 'Does it match our tone of voice?',
              a: 'Yes. It drafts in your brand voice, and agents can adjust the tone with one click before sending: friendlier, firmer, or shorter.',
            },
            {
              q: 'Where does it live?',
              a: 'Inside the helpdesk you already use. It reads the ticket your agent has open and assists in place, with zero tab-switching.',
            },
            {
              q: 'Does it learn from my team?',
              a: 'Yes. Edits your agents make to its suggestions feed back into the shared brain, so drafts keep getting closer to send-ready.',
            },
          ],
        },
      ],
      cta: { label: 'See it in action', href: '/demo' },
    },
  },
  {
    id: 'qa-agent',
    icon: ShieldCheck,
    title: 'QA Agent',
    description:
      'QA on everything. It scores every conversation, AI and human, against your standards, automatically.',
    modal: {
      eyebrow: 'QA Agent',
      headline: '100% coverage, zero sampling',
      lead: 'Traditional QA reviews 1–2% of conversations and hopes the rest look the same. The QA Agent reviews all of them: every channel, every agent, human or AI.',
      blocks: [
        {
          type: 'text',
          body: [
            'It grades each conversation against your own rubric for accuracy, tone, compliance, and resolution, then flags the ones that need a human look. Instead of auditing random tickets, your team reviews a ranked list of what actually went wrong.',
            'Because it covers AI conversations too, it is the safety net that lets you scale automation with confidence: any drift in quality shows up in the numbers before it shows up in your CSAT.',
          ],
        },
        {
          type: 'stats',
          items: [
            { value: '100%', label: 'Conversations scored' },
            { value: '0', label: 'Sampling' },
            { value: 'AI + human', label: 'Both sides covered' },
          ],
        },
        {
          type: 'list',
          heading: 'What it checks',
          items: [
            'Accuracy of every answer against your knowledge base',
            'Tone and brand-voice adherence',
            'Compliance phrases, disclosures, and policy rules',
            'Resolution quality: was the issue actually solved?',
            'Escalations: were the right ones made, at the right time?',
            'Trends by agent, team, channel, and topic',
          ],
        },
        {
          type: 'text',
          heading: 'From scores to improvements',
          body: 'Scores are only useful if they change something. Failed checks feed straight back into coaching queues for humans and knowledge or instruction fixes for the AI, so the same mistake stops repeating instead of just being counted.',
        },
        {
          type: 'faq',
          heading: 'Frequently asked',
          items: [
            {
              q: 'Does it review AI conversations too?',
              a: 'Yes. Every AI and human conversation gets scored on the same rubric. That’s what lets you scale automation with confidence.',
            },
            {
              q: 'Can I use my own QA rubric?',
              a: 'Yes. Bring your existing scorecard or build one in plain language: accuracy, tone, compliance, resolution, or anything specific to your business.',
            },
            {
              q: 'How do my QA reviewers work with it?',
              a: 'They stop sampling randomly. The agent flags outliers ranked by severity, so humans review the conversations that actually need eyes.',
            },
            {
              q: 'Does it work across all channels?',
              a: 'Yes: chat, voice (with transcripts), email, WhatsApp, and social are all scored with the same standards.',
            },
          ],
        },
      ],
      cta: { label: 'See it in action', href: '/demo' },
    },
  },
  {
    id: 'customer-understanding-agent',
    icon: BarChart3,
    title: 'Customer Understanding Agent',
    description:
      'Analyzes the voice of your customers across every conversation: trends, sentiment, and what to fix next.',
    modal: {
      eyebrow: 'Customer Understanding Agent',
      headline: 'Your customers are telling you what to build',
      lead: 'Every support conversation is feedback. The Customer Understanding Agent reads all of it and turns millions of messages into a clear picture of what customers want, love, and struggle with.',
      blocks: [
        {
          type: 'text',
          body: [
            'It clusters conversations into themes, tracks sentiment over time, and surfaces emerging issues while they are still small: a confusing new feature, a broken flow, a competitor coming up in every third call.',
            'Instead of skimming tickets or running surveys nobody answers, your product and CX teams get a live, ranked view of the voice of the customer, grounded in what customers actually said.',
          ],
        },
        {
          type: 'list',
          heading: 'What it surfaces',
          items: [
            'Voice-of-customer themes across every channel',
            'Sentiment and CSAT trends over time',
            'Emerging issues detected before they spike',
            'Product feedback ranked by frequency and impact',
            'Churn signals and at-risk account patterns',
            'Competitor mentions and pricing objections',
          ],
        },
        {
          type: 'stats',
          items: [
            { value: '100%', label: 'Conversations analyzed' },
            { value: '24/7', label: 'Trend monitoring' },
            { value: '0', label: 'Surveys required' },
          ],
        },
        {
          type: 'text',
          heading: 'Built for product and CX teams alike',
          body: 'Support sees the trends first; product hears about them last. This agent closes that gap with ranked, evidence-linked reports: every theme cites the actual conversations behind it, so “customers are confused by checkout” comes with the receipts.',
        },
        {
          type: 'faq',
          heading: 'Frequently asked',
          items: [
            {
              q: 'Where does the data come from?',
              a: 'Every conversation across every channel: chat, voice transcripts, email, WhatsApp, and social, analyzed automatically. No surveys needed.',
            },
            {
              q: 'How is this better than tagging tickets?',
              a: 'Manual tags are inconsistent and always behind. The agent clusters themes from the raw conversations themselves, so nothing depends on someone remembering to tag.',
            },
            {
              q: 'Can it alert me when something breaks?',
              a: 'Yes. Emerging-issue detection watches for unusual spikes in a theme and alerts your team while the issue is still small.',
            },
            {
              q: 'Who is it for?',
              a: 'CX leaders tracking quality and churn risk, and product teams who want feedback grounded in what customers actually said, with linked evidence.',
            },
          ],
        },
      ],
      cta: { label: 'See it in action', href: '/demo' },
    },
  },
  {
    id: 'apps-agent',
    icon: Blocks,
    title: 'Apps Agent',
    description:
      'Extend Open by prompting. Describe the flow or feature you need, and it builds it into your workspace.',
    modal: {
      eyebrow: 'Apps Agent',
      headline: 'If you can describe it, you can ship it',
      lead: 'Every support org has needs no platform ships out of the box. The Apps Agent lets you build them yourself: new flows, integrations, and features in Open, created by prompting.',
      blocks: [
        {
          type: 'text',
          body: [
            'Describe what you need in plain language, like “check order status in our internal API before replying”, “add a returns flow that follows our policy”, or “build a dashboard for refund approvals”, and the Apps Agent builds it into your Open workspace.',
            'What used to be a ticket to engineering becomes a prompt. Your ops team extends the platform at the speed of a conversation, with every change versioned and reversible.',
          ],
        },
        {
          type: 'list',
          heading: 'What teams build with it',
          items: [
            'Custom workflows that call your internal APIs',
            'Returns, refunds, and approval flows with your policies',
            'Dashboards and views for your team’s exact process',
            'Integrations with in-house and long-tail systems',
            'Custom escalation logic and routing rules',
            'New features on top of your support stack, no code needed',
          ],
        },
        {
          type: 'stats',
          items: [
            { value: '0', label: 'Code required' },
            { value: 'Minutes', label: 'From idea to live' },
            { value: '1-click', label: 'Rollback' },
          ],
        },
        {
          type: 'text',
          heading: 'Power without the risk',
          body: 'Everything the Apps Agent builds runs sandboxed, versioned, and audit-logged. Test a flow before it goes live, see exactly what changed and who prompted it, and roll anything back in one click. It is the speed of prompting with the guardrails of production software.',
        },
        {
          type: 'faq',
          heading: 'Frequently asked',
          items: [
            {
              q: 'Do I need engineers to use it?',
              a: 'No. You describe what you need in plain language and the Apps Agent builds it. Engineers can drop into code only when logic gets genuinely complex.',
            },
            {
              q: 'What can it connect to?',
              a: 'Your internal APIs, databases, and any system with a REST interface, alongside the 100+ native integrations already in Open.',
            },
            {
              q: 'What if it builds something wrong?',
              a: 'Every change is versioned, testable before going live, and reversible in one click, with a full audit trail of what changed and who prompted it.',
            },
            {
              q: 'How is this different from a workflow builder?',
              a: 'A workflow builder gives you blocks; the Apps Agent builds the whole thing from a description, including flows, views, and features, and you refine it by prompting.',
            },
          ],
        },
      ],
      cta: { label: 'See it in action', href: '/demo' },
    },
  },
];

export const Products = () => {
  const { locale } = useTranslation();
  const isAr = locale === 'ar';
  const section = isAr
    ? PRODUCTS_SECTION_AR
    : {
        title: 'Six agents, one platform',
        subtitle: 'Unify your customer experience with AI.',
      };

  return (
    <BentoGrid
      title={section.title}
      subtitle={section.subtitle}
      cards={isAr ? PRODUCTS_AR : CARDS}
      // These six are the only cards on the homepage that open a modal, and
      // nothing else says so — so the affordance stays visible here rather
      // than waiting for a hover that never happens on touch.
      alwaysShowExpand
    />
  );
};

export default Products;
